import React, { useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import logo from '../../images/logo-kusina.png';
import {
  PageWrapper,
  FormContainer,
  PageTitle,
  PageSubtitle,
  Section,
  SectionTitle,
  FieldGrid,
  FieldGroup,
  Label,
  Input,
  Select,
  InputWithButton,
  SearchButton,
  ErrorText,
  AlertBox,
  SubmitButton,
  Spinner,
} from './Afiliaciones.elements';

const RUC_API = '/api/ruc?ruc=';
const DNI_API = '/api/dni?dni=';
const SUBMIT_API = '/api/afiliaciones';

const BANCOS = [
  'BCP - Banco de Crédito del Perú',
  'BBVA Perú',
  'Interbank',
  'Scotiabank',
  'BanBif',
  'Banco Pichincha',
  'Mibanco',
  'Banco GNB',
  'Banco Falabella',
  'Banco Ripley',
];

const initialForm = {
  direccion: '',
  ruc: '',
  razonSocial: '',
  tipoDocumento: 'DNI',
  numeroDocumento: '',
  nombreRepresentante: '',
  telefono: '',
  email: '',
  banco: '',
  numeroCuenta: '',
  cci: '',
};

const onlyDigits = (value) => value.replace(/\D/g, '');

const isFormComplete = (form) =>
  form.ruc.trim() &&
  form.razonSocial.trim() &&
  form.direccion.trim() &&
  form.numeroDocumento.trim() &&
  form.nombreRepresentante.trim() &&
  form.telefono.trim() &&
  form.email.trim() &&
  form.banco.trim() &&
  form.numeroCuenta.trim() &&
  form.cci.trim();

const Afiliaciones = () => {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const [rucLoading, setRucLoading] = useState(false);
  const [rucAlert, setRucAlert] = useState(null);

  const [dniLoading, setDniLoading] = useState(false);
  const [dniAlert, setDniAlert] = useState(null);

  const [submitLoading, setSubmitLoading] = useState(false);

  const handleChange = (field, numericOnly = false) => (e) => {
    const value = numericOnly ? onlyDigits(e.target.value) : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const buscarRuc = async () => {
    if (!form.ruc.trim()) {
      setErrors((prev) => ({ ...prev, ruc: 'Ingrese un RUC' }));
      return;
    }
    setRucLoading(true);
    setRucAlert(null);
    setForm((prev) => ({ ...prev, razonSocial: '', direccion: '' }));
    try {
      const res = await fetch(`${RUC_API}${form.ruc.trim()}`);
      const text = await res.text();
      let json;
      try { json = JSON.parse(text); } catch {
        setRucAlert({ type: 'error', message: `HTTP ${res.status} — Respuesta inválida: ${text.slice(0, 200)}` });
        return;
      }
      if (!res.ok) {
        setRucAlert({ type: 'error', message: `HTTP ${res.status} — ${json?.error?.detail || text.slice(0, 200)}` });
      } else if (json.error?.code !== 0 || !json.data?.razonSocial) {
        setRucAlert({ type: 'error', message: `RUC no encontrado. (code: ${json.error?.code} — ${json.error?.detail || 'sin detalle'})` });
      } else {
        setForm((prev) => ({
          ...prev,
          razonSocial: json.data.razonSocial,
          direccion: json.data.direccion || '',
        }));
        setRucAlert({ type: 'success', message: `Empresa encontrada: ${json.data.estado} - ${json.data.condicion}` });
      }
    } catch (err) {
      setRucAlert({ type: 'error', message: `Error de red: ${err.message}` });
    } finally {
      setRucLoading(false);
    }
  };

  const buscarDni = async () => {
    if (!form.numeroDocumento.trim()) {
      setErrors((prev) => ({ ...prev, numeroDocumento: 'Ingrese un número de documento' }));
      return;
    }
    setDniLoading(true);
    setDniAlert(null);
    setForm((prev) => ({ ...prev, nombreRepresentante: '' }));
    try {
      const res = await fetch(`${DNI_API}${form.numeroDocumento.trim()}`);
      const text = await res.text();
      let json;
      try { json = JSON.parse(text); } catch {
        setDniAlert({ type: 'error', message: `HTTP ${res.status} — Respuesta inválida: ${text.slice(0, 200)}` });
        return;
      }
      if (!res.ok) {
        setDniAlert({ type: 'error', message: `HTTP ${res.status} — ${json?.error?.detail || text.slice(0, 200)}` });
      } else if (json.error?.code !== 0 || !json.data?.nombres) {
        setDniAlert({ type: 'error', message: `DNI no encontrado. (code: ${json.error?.code} — ${json.error?.detail || 'sin detalle'})` });
      } else {
        const nombre = `${json.data.nombres} ${json.data.apellidoPaterno} ${json.data.apellidoMaterno}`.trim();
        setForm((prev) => ({ ...prev, nombreRepresentante: nombre }));
        setDniAlert({ type: 'success', message: 'Representante legal encontrado.' });
      }
    } catch (err) {
      setDniAlert({ type: 'error', message: `Error de red: ${err.message}` });
    } finally {
      setDniLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.direccion.trim()) newErrors.direccion = 'Campo requerido';
    if (!form.ruc.trim()) newErrors.ruc = 'Campo requerido';
    if (!form.razonSocial.trim()) newErrors.razonSocial = 'Busque el RUC para autocompletar';
    if (!form.numeroDocumento.trim()) newErrors.numeroDocumento = 'Campo requerido';
    if (!form.nombreRepresentante.trim()) newErrors.nombreRepresentante = 'Busque el DNI para autocompletar';
    if (!form.telefono.trim()) newErrors.telefono = 'Campo requerido';
    if (!form.email.trim()) newErrors.email = 'Campo requerido';
    if (!form.banco.trim()) newErrors.banco = 'Seleccione un banco';
    if (!form.numeroCuenta.trim()) newErrors.numeroCuenta = 'Campo requerido';
    if (!form.cci.trim()) newErrors.cci = 'Campo requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitLoading(true);
    try {
      const ipRes = await fetch('/api/ip');
      const { ip } = await ipRes.json();

      const res = await fetch(SUBMIT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          ip_usuario: ip,
          ruc: form.ruc,
          razon_social: form.razonSocial,
          direccion: form.direccion,
          tipo_documento: form.tipoDocumento,
          numero_documento: form.numeroDocumento,
          nombre_representante: form.nombreRepresentante,
          telefono: form.telefono,
          email: form.email,
          banco: form.banco,
          numero_cuenta: form.numeroCuenta,
          cci: form.cci,
        }),
      });
      if (!res.ok) throw new Error('Error en el servidor');
      alert('¡Solicitud de afiliación enviada correctamente!');
      setForm(initialForm);
    } catch {
      alert('Ocurrió un error al enviar la solicitud. Por favor, inténtelo nuevamente.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const formComplete = isFormComplete(form);

  return (
    <PageWrapper>
      <FormContainer>
        <img src={logo} alt="Kusina" style={{ height: 52, marginBottom: 24 }} />
        <PageTitle>Formulario de Afiliación</PageTitle>
        <PageSubtitle>Completa los datos de tu empresa para iniciar el proceso de afiliación a Kusina.</PageSubtitle>

        <form onSubmit={handleSubmit} noValidate>
          {/* Datos de la empresa */}
          <Section>
            <SectionTitle>Datos de la empresa</SectionTitle>
            <FieldGrid cols={1}>
              <FieldGroup>
                <Label htmlFor="ruc">RUC</Label>
                <InputWithButton>
                  <Input
                    id="ruc"
                    type="text"
                    inputMode="numeric"
                    placeholder="Ej. 20601478359"
                    value={form.ruc}
                    onChange={handleChange('ruc', true)}
                    maxLength={11}
                    error={errors.ruc ? 1 : 0}
                  />
                  <SearchButton type="button" onClick={buscarRuc} disabled={rucLoading}>
                    {rucLoading ? <Spinner /> : <FaSearch />}
                    {rucLoading ? 'Buscando...' : 'Buscar'}
                  </SearchButton>
                </InputWithButton>
                {errors.ruc && <ErrorText>{errors.ruc}</ErrorText>}
                {rucAlert && <AlertBox type={rucAlert.type}>{rucAlert.message}</AlertBox>}
              </FieldGroup>

              <FieldGroup>
                <Label htmlFor="razonSocial">Razón Social</Label>
                <Input
                  id="razonSocial"
                  type="text"
                  placeholder="Se autocompleta al buscar el RUC"
                  value={form.razonSocial}
                  readOnly
                  disabled
                  error={errors.razonSocial ? 1 : 0}
                />
                {errors.razonSocial && <ErrorText>{errors.razonSocial}</ErrorText>}
              </FieldGroup>

              <FieldGroup>
                <Label htmlFor="direccion">Dirección del local</Label>
                <Input
                  id="direccion"
                  type="text"
                  placeholder="Ej. Av. Javier Prado Este 4200, San Borja"
                  value={form.direccion}
                  onChange={handleChange('direccion')}
                  error={errors.direccion ? 1 : 0}
                />
                {errors.direccion && <ErrorText>{errors.direccion}</ErrorText>}
              </FieldGroup>
            </FieldGrid>
          </Section>

          {/* Representante legal */}
          <Section>
            <SectionTitle>Datos del representante legal</SectionTitle>
            <FieldGrid cols={1}>
              <FieldGrid cols={2}>
                <FieldGroup>
                  <Label htmlFor="tipoDocumento">Tipo de documento</Label>
                  <Select
                    id="tipoDocumento"
                    value={form.tipoDocumento}
                    onChange={handleChange('tipoDocumento')}
                  >
                    <option value="DNI">DNI</option>
                  </Select>
                </FieldGroup>

                <FieldGroup>
                  <Label htmlFor="numeroDocumento">Número de documento</Label>
                  <InputWithButton>
                    <Input
                      id="numeroDocumento"
                      type="text"
                      inputMode="numeric"
                      placeholder="Ej. 70457868"
                      value={form.numeroDocumento}
                      onChange={handleChange('numeroDocumento', true)}
                      maxLength={8}
                      error={errors.numeroDocumento ? 1 : 0}
                    />
                    <SearchButton type="button" onClick={buscarDni} disabled={dniLoading}>
                      {dniLoading ? <Spinner /> : <FaSearch />}
                      {dniLoading ? 'Buscando...' : 'Buscar'}
                    </SearchButton>
                  </InputWithButton>
                  {errors.numeroDocumento && <ErrorText>{errors.numeroDocumento}</ErrorText>}
                  {dniAlert && <AlertBox type={dniAlert.type}>{dniAlert.message}</AlertBox>}
                </FieldGroup>
              </FieldGrid>

              <FieldGroup>
                <Label htmlFor="nombreRepresentante">Nombre del representante legal</Label>
                <Input
                  id="nombreRepresentante"
                  type="text"
                  placeholder="Se autocompleta al buscar el DNI"
                  value={form.nombreRepresentante}
                  readOnly
                  disabled
                  error={errors.nombreRepresentante ? 1 : 0}
                />
                {errors.nombreRepresentante && <ErrorText>{errors.nombreRepresentante}</ErrorText>}
              </FieldGroup>

              <FieldGrid cols={2}>
                <FieldGroup>
                  <Label htmlFor="telefono">Teléfono de contacto</Label>
                  <Input
                    id="telefono"
                    type="tel"
                    placeholder="Ej. 987654321"
                    value={form.telefono}
                    onChange={handleChange('telefono')}
                    maxLength={15}
                    error={errors.telefono ? 1 : 0}
                  />
                  {errors.telefono && <ErrorText>{errors.telefono}</ErrorText>}
                </FieldGroup>

                <FieldGroup>
                  <Label htmlFor="email">Correo electrónico</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Ej. contacto@empresa.com"
                    value={form.email}
                    onChange={handleChange('email')}
                    error={errors.email ? 1 : 0}
                  />
                  {errors.email && <ErrorText>{errors.email}</ErrorText>}
                </FieldGroup>
              </FieldGrid>
            </FieldGrid>
          </Section>

          {/* Datos de pago */}
          <Section>
            <SectionTitle>Datos de pago</SectionTitle>
            <FieldGrid cols={1}>
              <FieldGroup>
                <Label htmlFor="banco">Banco</Label>
                <Select
                  id="banco"
                  value={form.banco}
                  onChange={handleChange('banco')}
                  error={errors.banco ? 1 : 0}
                >
                  <option value="">Seleccione un banco</option>
                  {BANCOS.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </Select>
                {errors.banco && <ErrorText>{errors.banco}</ErrorText>}
              </FieldGroup>

              <FieldGrid cols={2}>
                <FieldGroup>
                  <Label htmlFor="numeroCuenta">Número de cuenta</Label>
                  <Input
                    id="numeroCuenta"
                    type="text"
                    inputMode="numeric"
                    placeholder="Ej. 19451234567890"
                    value={form.numeroCuenta}
                    onChange={handleChange('numeroCuenta', true)}
                    error={errors.numeroCuenta ? 1 : 0}
                  />
                  {errors.numeroCuenta && <ErrorText>{errors.numeroCuenta}</ErrorText>}
                </FieldGroup>

                <FieldGroup>
                  <Label htmlFor="cci">Número CCI</Label>
                  <Input
                    id="cci"
                    type="text"
                    inputMode="numeric"
                    placeholder="Ej. 00219400194512345678900"
                    value={form.cci}
                    onChange={handleChange('cci', true)}
                    error={errors.cci ? 1 : 0}
                  />
                  {errors.cci && <ErrorText>{errors.cci}</ErrorText>}
                </FieldGroup>
              </FieldGrid>
            </FieldGrid>
          </Section>

          <SubmitButton type="submit" disabled={!formComplete || submitLoading}>
            {submitLoading ? 'Enviando solicitud...' : 'Enviar solicitud de afiliación'}
          </SubmitButton>
        </form>
      </FormContainer>
    </PageWrapper>
  );
};

export default Afiliaciones;
